import React, { useState, useMemo } from 'react';
import {
  X,
  Calendar,
  Search,
  Filter,
  Archive,
  ShoppingBag,
  Store,
  Gavel,
  ExternalLink,
  Clock,
  AlertCircle,
  CheckCircle2,
  DollarSign,
  User,
  ArrowUpDown,
  RefreshCw,
  FileSpreadsheet,
  Download,
  Info
} from 'lucide-react';
import {
  HistoryOrderItem,
  buildUnifiedHistoryList,
  isOrderOlderThan14Days,
  PAYMENT_DEADLINE_DAYS,
  ARCHIVE_THRESHOLD_DAYS
} from '../lib/orderExpirationService';
import { UserProfile, PersonalShopperOrder, StoreProduct, YahooAuctionBid, PackageItem } from '../types';

interface OrderHistoryArchiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: UserProfile;
  personalShopperOrders: PersonalShopperOrder[];
  storeProducts: StoreProduct[];
  yahooBids: YahooAuctionBid[];
  packages?: PackageItem[];
  defaultOnlyArchived?: boolean;
}

export const OrderHistoryArchiveModal: React.FC<OrderHistoryArchiveModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  personalShopperOrders,
  storeProducts,
  yahooBids,
  packages = [],
  defaultOnlyArchived = false
}) => {
  const isAdmin = currentUser.role === 'admin';

  // Filters state
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'personal_shopper' | 'store' | 'yahoo_auction'>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'paid' | 'cancelled' | 'pending'>('ALL');
  const [viewScope, setViewScope] = useState<'archived_only' | 'all'>(defaultOnlyArchived ? 'archived_only' : 'all');
  const [suiteFilter, setSuiteFilter] = useState<string>('ALL');

  // Selected item for detail view
  const [selectedItem, setSelectedItem] = useState<HistoryOrderItem | null>(null);

  // Build unified list
  const allHistory = useMemo(() => {
    return buildUnifiedHistoryList(personalShopperOrders, storeProducts, yahooBids, packages);
  }, [personalShopperOrders, storeProducts, yahooBids, packages]);

  // Client vs Admin scope
  const scopedHistory = useMemo(() => {
    if (isAdmin) {
      if (suiteFilter !== 'ALL') {
        return allHistory.filter((item) => (item.suiteNumber || '').toUpperCase() === suiteFilter.toUpperCase());
      }
      return allHistory;
    }

    // Client sees only their own orders
    const userSuite = (currentUser.suiteNumber || '').toUpperCase();
    const userUid = currentUser.uid;
    const userEmail = (currentUser.email || '').toLowerCase();

    return allHistory.filter((item) => {
      const matchSuite = item.suiteNumber && userSuite && item.suiteNumber.toUpperCase() === userSuite;
      const matchUid = item.userId && userUid && item.userId === userUid;
      const matchEmail = item.clientEmail && userEmail && item.clientEmail.toLowerCase() === userEmail;
      return matchSuite || matchUid || matchEmail;
    });
  }, [allHistory, isAdmin, suiteFilter, currentUser]);

  // Distinct suites for admin filter
  const availableSuites = useMemo(() => {
    if (!isAdmin) return [];
    const set = new Set<string>();
    allHistory.forEach((item) => {
      if (item.suiteNumber && item.suiteNumber !== 'N/A') {
        set.add(item.suiteNumber.toUpperCase());
      }
    });
    return Array.from(set).sort();
  }, [allHistory, isAdmin]);

  // Apply Search & Date Filters
  const filteredHistory = useMemo(() => {
    return scopedHistory.filter((item) => {
      // 1. View Scope: Archived only (+14 days) or All
      if (viewScope === 'archived_only' && !item.isOlderThan14Days) {
        return false;
      }

      // 2. Type Filter
      if (typeFilter !== 'ALL' && item.type !== typeFilter) {
        return false;
      }

      // 3. Status Filter
      if (statusFilter !== 'ALL') {
        if (statusFilter === 'paid') {
          if (item.statusBadgeType !== 'success' && !item.status.toLowerCase().includes('pago')) return false;
        } else if (statusFilter === 'cancelled') {
          if (item.statusBadgeType !== 'cancelled' && !item.status.toLowerCase().includes('cancel')) return false;
        } else if (statusFilter === 'pending') {
          if (item.statusBadgeType !== 'pending' && !item.status.toLowerCase().includes('aguardando')) return false;
        }
      }

      // 4. Date Range Filter
      if (startDate) {
        const itemDate = new Date(item.createdAt).getTime();
        const start = new Date(`${startDate}T00:00:00`).getTime();
        if (itemDate < start) return false;
      }

      if (endDate) {
        const itemDate = new Date(item.createdAt).getTime();
        const end = new Date(`${endDate}T23:59:59`).getTime();
        if (itemDate > end) return false;
      }

      // 5. Search Term Filter
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase().trim();
        const titleMatch = (item.title || '').toLowerCase().includes(q);
        const origTitleMatch = (item.originalJapaneseTitle || '').toLowerCase().includes(q);
        const storeMatch = (item.storeName || '').toLowerCase().includes(q);
        const suiteMatch = (item.suiteNumber || '').toLowerCase().includes(q);
        const clientMatch = (item.clientName || '').toLowerCase().includes(q);
        const idMatch = (item.id || '').toLowerCase().includes(q);
        const notesMatch = (item.notes || '').toLowerCase().includes(q);

        if (!titleMatch && !origTitleMatch && !storeMatch && !suiteMatch && !clientMatch && !idMatch && !notesMatch) {
          return false;
        }
      }

      return true;
    });
  }, [scopedHistory, viewScope, typeFilter, statusFilter, startDate, endDate, searchTerm]);

  // Statistics counters
  const stats = useMemo(() => {
    let totalJpy = 0;
    let totalBrl = 0;
    let olderCount = 0;
    let expiredCancelledCount = 0;

    scopedHistory.forEach((item) => {
      if (item.isOlderThan14Days) olderCount++;
      if (item.isExpiredCancelled) expiredCancelledCount++;
      if (item.statusBadgeType === 'success') {
        totalJpy += item.priceJpy || item.totalJpy || 0;
        totalBrl += item.totalBrl || 0;
      }
    });

    return {
      total: scopedHistory.length,
      olderCount,
      expiredCancelledCount,
      totalJpy,
      totalBrl
    };
  }, [scopedHistory]);

  // Quick Date Presets
  const applyDatePreset = (preset: '30days' | '90days' | '180days' | 'year' | 'clear') => {
    if (preset === 'clear') {
      setStartDate('');
      setEndDate('');
      return;
    }

    const end = new Date();
    const start = new Date();

    if (preset === '30days') {
      start.setDate(end.getDate() - 30);
    } else if (preset === '90days') {
      start.setDate(end.getDate() - 90);
    } else if (preset === '180days') {
      start.setDate(end.getDate() - 180);
    } else if (preset === 'year') {
      start.setFullYear(end.getFullYear(), 0, 1);
    }

    setStartDate(start.toISOString().substring(0, 10));
    setEndDate(end.toISOString().substring(0, 10));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 backdrop-blur-md p-2 sm:p-4 overflow-y-auto animate-fade-in">
      <div className="bg-white w-full max-w-5xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col my-4 max-h-[92vh]">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-5 sm:p-6 relative shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
            title="Fechar"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/20 text-white shadow-inner">
              <Archive className="w-6 h-6 text-indigo-300" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-black tracking-tight">Área de Histórico & Arquivo de Pedidos</h3>
                <span className="px-2.5 py-0.5 bg-indigo-500/20 text-indigo-200 font-bold text-[10px] rounded-full border border-indigo-400/30">
                  {isAdmin ? 'Visão Administrativa Geral' : `Suíte #${currentUser.suiteNumber || 'DJ'}`}
                </span>
              </div>
              <p className="text-slate-300 text-xs sm:text-sm mt-0.5">
                Pesquise pedidos antigos de <strong>Personal Shopper</strong>, <strong>Loja Do Japão</strong> e <strong>Leilão Yahoo</strong> por data ou termo.
              </p>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-5 pt-4 border-t border-white/10 text-xs">
            <div className="bg-white/5 border border-white/10 p-2.5 rounded-xl">
              <span className="text-slate-400 block text-[10px] font-bold uppercase">Total Registrado</span>
              <strong className="text-white text-base">{stats.total} pedidos</strong>
            </div>

            <div className="bg-white/5 border border-white/10 p-2.5 rounded-xl">
              <span className="text-amber-300 block text-[10px] font-bold uppercase">Arquivados (+14 dias)</span>
              <strong className="text-amber-300 text-base">{stats.olderCount} pedidos</strong>
            </div>

            <div className="bg-white/5 border border-white/10 p-2.5 rounded-xl">
              <span className="text-rose-300 block text-[10px] font-bold uppercase">Cancelados (5 dias)</span>
              <strong className="text-rose-300 text-base">{stats.expiredCancelledCount} pedidos</strong>
            </div>

            <div className="bg-white/5 border border-white/10 p-2.5 rounded-xl">
              <span className="text-emerald-300 block text-[10px] font-bold uppercase">Total Concluído (JPY)</span>
              <strong className="text-emerald-300 text-base">¥{stats.totalJpy.toLocaleString()}</strong>
            </div>
          </div>
        </div>

        {/* Filters Toolbar */}
        <div className="p-4 sm:p-5 bg-slate-50 border-b border-slate-200 space-y-3 shrink-0">
          
          {/* Row 1: Search by Text & Scope Toggle */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome do produto, loja, suíte, cliente ou #ID..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-300 rounded-2xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Scope Toggle: Arquivados (+14 dias) vs Todos */}
            <div className="inline-flex rounded-2xl border border-slate-300 bg-white p-1 shadow-xs shrink-0 self-start sm:self-auto">
              <button
                type="button"
                onClick={() => setViewScope('archived_only')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                  viewScope === 'archived_only'
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Archive className="w-3.5 h-3.5" />
                <span>Arquivo (+14 dias)</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${
                  viewScope === 'archived_only' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'
                }`}>
                  {stats.olderCount}
                </span>
              </button>
              
              <button
                type="button"
                onClick={() => setViewScope('all')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                  viewScope === 'all'
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span>Histórico Completo</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${
                  viewScope === 'all' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'
                }`}>
                  {stats.total}
                </span>
              </button>
            </div>

          </div>

          {/* Row 2: Search by Date (Data Inicial e Data Final) + Type + Status Filters */}
          <div className="flex flex-wrap items-center gap-3">
            
            {/* Date Range: Data Inicial */}
            <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 border border-slate-300 rounded-xl shadow-xs">
              <Calendar className="w-3.5 h-3.5 text-indigo-600" />
              <span className="text-[11px] font-bold text-slate-500">De:</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="text-xs font-mono font-semibold text-slate-800 bg-transparent focus:outline-none"
              />
            </div>

            {/* Date Range: Data Final */}
            <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 border border-slate-300 rounded-xl shadow-xs">
              <Calendar className="w-3.5 h-3.5 text-indigo-600" />
              <span className="text-[11px] font-bold text-slate-500">Até:</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="text-xs font-mono font-semibold text-slate-800 bg-transparent focus:outline-none"
              />
            </div>

            {/* Quick Date Presets */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => applyDatePreset('30days')}
                className="px-2 py-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-[10px] font-bold transition-colors shadow-xs"
              >
                30 Dias
              </button>
              <button
                type="button"
                onClick={() => applyDatePreset('90days')}
                className="px-2 py-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-[10px] font-bold transition-colors shadow-xs"
              >
                90 Dias
              </button>
              <button
                type="button"
                onClick={() => applyDatePreset('year')}
                className="px-2 py-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-[10px] font-bold transition-colors shadow-xs"
              >
                Este Ano
              </button>
              {(startDate || endDate) && (
                <button
                  type="button"
                  onClick={() => applyDatePreset('clear')}
                  className="px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-lg text-[10px] font-bold transition-colors"
                >
                  Limpar Datas
                </button>
              )}
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-1 ml-auto">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
                className="px-3 py-1.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs"
              >
                <option value="ALL">Todos os Tipos</option>
                <option value="personal_shopper">🛒 Personal Shopper</option>
                <option value="store">🏪 Loja Do Japão</option>
                <option value="yahoo_auction">🔨 Leilão Yahoo</option>
              </select>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-3 py-1.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs"
              >
                <option value="ALL">Todos os Status</option>
                <option value="paid">✅ Concluídos / Pagos</option>
                <option value="cancelled">❌ Cancelados / Expirados (5d)</option>
                <option value="pending">⏳ Aguardando Pagamento</option>
              </select>

              {/* Admin Suite Filter */}
              {isAdmin && availableSuites.length > 0 && (
                <select
                  value={suiteFilter}
                  onChange={(e) => setSuiteFilter(e.target.value)}
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded-xl text-xs font-mono font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs"
                >
                  <option value="ALL">Todas as Suítes</option>
                  {availableSuites.map((s) => (
                    <option key={s} value={s}>Suíte #{s}</option>
                  ))}
                </select>
              )}
            </div>

          </div>

        </div>

        {/* Content Body: Orders List */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 bg-slate-50/50">
          
          {/* Active Rules Banner */}
          <div className="p-3 bg-indigo-50/80 border border-indigo-200/80 rounded-2xl flex items-center justify-between text-xs text-indigo-900 gap-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-indigo-600 shrink-0" />
              <span>
                <strong>Regras do Sistema:</strong> Limite de <strong>5 dias</strong> para pagamento na loja e compra assistida (cancelamento automático se não pago). Pedidos com <strong>mais de 14 dias</strong> são arquivados nesta área para manter a lista visual limpa e ágil.
              </span>
            </div>
            <span className="font-bold shrink-0 text-indigo-700">
              {filteredHistory.length} encontrados
            </span>
          </div>

          {filteredHistory.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-3xl border-2 border-dashed border-slate-200 space-y-3 my-6">
              <Archive className="w-12 h-12 text-slate-300 mx-auto" />
              <h4 className="text-base font-bold text-slate-800">
                Nenhum pedido encontrado nos critérios selecionados
              </h4>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                Tente ajustar as datas inicial e final, trocar os filtros de status ou limpar a pesquisa para encontrar pedidos arquivados.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setStartDate('');
                  setEndDate('');
                  setTypeFilter('ALL');
                  setStatusFilter('ALL');
                  setViewScope('all');
                }}
                className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl shadow-xs hover:bg-slate-800 transition-all inline-flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Redefinir Filtros de Pesquisa</span>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredHistory.map((item) => {
                const dateFormatted = new Date(item.createdAt).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                });
                const timeFormatted = new Date(item.createdAt).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit'
                });

                return (
                  <div
                    key={item.id}
                    className={`p-4 sm:p-5 bg-white rounded-2xl border transition-all hover:shadow-md space-y-3 ${
                      item.statusBadgeType === 'cancelled'
                        ? 'border-slate-200 bg-slate-50/40 opacity-90'
                        : item.statusBadgeType === 'success'
                        ? 'border-emerald-200'
                        : 'border-slate-200'
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      
                      {/* Left: Category Icon, ID, Store, Suite, Date */}
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0 border ${
                          item.type === 'personal_shopper' ? 'bg-indigo-50 border-indigo-200' :
                          item.type === 'store' ? 'bg-emerald-50 border-emerald-200' :
                          'bg-amber-50 border-amber-200'
                        }`}>
                          {item.typeIcon}
                        </div>

                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
                              #{item.id}
                            </span>
                            <span className="text-xs font-bold text-slate-700">
                              {item.typeName}
                            </span>
                            <span className="text-xs text-slate-400">•</span>
                            <span className="text-xs font-semibold text-slate-500">
                              {item.storeName}
                            </span>
                            <span className="text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100">
                              Suíte #{item.suiteNumber}
                            </span>
                            {item.isOlderThan14Days && (
                              <span className="text-[10px] font-black uppercase text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full border border-amber-200">
                                🗄️ Arquivado (+14d)
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-1">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            <span>Data do Pedido: <strong>{dateFormatted} às {timeFormatted}</strong></span>
                            {item.clientName && isAdmin && (
                              <>
                                <span>•</span>
                                <span>Cliente: <strong>{item.clientName}</strong></span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right: Status Badge & Amount */}
                      <div className="flex flex-col items-end gap-1.5">
                        <span className={`px-2.5 py-1 rounded-xl text-xs font-black border ${
                          item.statusBadgeType === 'success'
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : item.statusBadgeType === 'cancelled'
                            ? 'bg-rose-50 text-rose-800 border-rose-200'
                            : item.statusBadgeType === 'processing'
                            ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
                            : 'bg-amber-50 text-amber-800 border-amber-200'
                        }`}>
                          {item.statusLabel}
                        </span>

                        <div className="text-right">
                          <strong className="text-sm font-black text-slate-900 block">
                            ¥{(item.totalJpy || item.priceJpy || 0).toLocaleString()}
                          </strong>
                          {item.totalBrl ? (
                            <span className="text-[11px] text-emerald-700 font-bold">
                              R$ {item.totalBrl.toFixed(2)}
                            </span>
                          ) : null}
                        </div>
                      </div>

                    </div>

                    {/* Middle: Product Thumbnail, Title, Notes */}
                    <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                      {item.imageUrl && (
                        <img
                          src={item.imageUrl}
                          alt={item.title}
                          className="w-14 h-14 object-cover rounded-xl border border-slate-200 shrink-0 bg-white"
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />
                      )}

                      <div className="flex-1 min-w-0">
                        <h5 className="text-xs font-bold text-slate-900 truncate">
                          {item.title}
                        </h5>
                        {item.originalJapaneseTitle && (
                          <p className="text-[11px] text-slate-500 font-mono truncate">
                            {item.originalJapaneseTitle}
                          </p>
                        )}
                        {item.notes && (
                          <p className="text-[11px] text-slate-600 mt-1 line-clamp-1 italic bg-slate-50 px-2 py-0.5 rounded-lg border border-slate-200/60 inline-block">
                            📝 {item.notes}
                          </p>
                        )}
                      </div>

                      {/* Action: Open link / View details */}
                      <div className="flex items-center gap-2 shrink-0">
                        {item.productUrl && (
                          <a
                            href={item.productUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-1 transition-colors"
                            title="Ver link original do produto"
                          >
                            <span>Link</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}

                        <button
                          type="button"
                          onClick={() => setSelectedItem(item)}
                          className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-bold flex items-center gap-1 transition-colors border border-indigo-200"
                        >
                          <Info className="w-3.5 h-3.5" />
                          <span>Detalhes</span>
                        </button>
                      </div>
                    </div>

                  </div>
                );
              })}
            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div className="bg-slate-50 p-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <Archive className="w-4 h-4 text-indigo-600" />
            <span>Exibindo <strong>{filteredHistory.length}</strong> de <strong>{scopedHistory.length}</strong> pedidos no histórico.</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-xs shadow-xs transition-colors"
            >
              Fechar Histórico
            </button>
          </div>
        </div>

      </div>

      {/* Detail Slide-over / Modal for Selected Item */}
      {selectedItem && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/60 p-4 animate-fade-in">
          <div className="bg-white max-w-lg w-full rounded-3xl shadow-2xl border border-slate-200 p-6 space-y-4 relative">
            <button
              onClick={() => setSelectedItem(null)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <span className="text-2xl">{selectedItem.typeIcon}</span>
              <div>
                <span className="text-[10px] font-black uppercase text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-200">
                  {selectedItem.typeName}
                </span>
                <h4 className="text-sm font-black text-slate-900 mt-1">Pedido #{selectedItem.id}</h4>
              </div>
            </div>

            {selectedItem.imageUrl && (
              <img
                src={selectedItem.imageUrl}
                alt={selectedItem.title}
                className="w-full h-44 object-cover rounded-2xl border border-slate-200"
              />
            )}

            <div className="space-y-2 text-xs">
              <div>
                <span className="text-slate-500 font-bold block text-[10px] uppercase">Produto</span>
                <p className="font-bold text-slate-900 text-sm">{selectedItem.title}</p>
                {selectedItem.originalJapaneseTitle && (
                  <p className="text-slate-500 font-mono mt-0.5">{selectedItem.originalJapaneseTitle}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Suíte</span>
                  <strong className="text-indigo-700">#{selectedItem.suiteNumber}</strong>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Loja</span>
                  <strong className="text-slate-800">{selectedItem.storeName}</strong>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Valor (JPY)</span>
                  <strong className="text-slate-900">¥{(selectedItem.totalJpy || selectedItem.priceJpy).toLocaleString()}</strong>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Status</span>
                  <span className={`px-2 py-0.5 rounded-md font-bold text-[11px] inline-block ${
                    selectedItem.statusBadgeType === 'success' ? 'bg-emerald-100 text-emerald-800' :
                    selectedItem.statusBadgeType === 'cancelled' ? 'bg-rose-100 text-rose-800' :
                    'bg-amber-100 text-amber-800'
                  }`}>
                    {selectedItem.statusLabel}
                  </span>
                </div>
              </div>

              {selectedItem.clientName && (
                <div className="pt-2 border-t border-slate-100">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Cliente</span>
                  <p className="font-semibold text-slate-800">{selectedItem.clientName} ({selectedItem.clientEmail || 'Sem email'})</p>
                </div>
              )}

              {selectedItem.notes && (
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <span className="text-slate-500 font-bold block text-[10px] uppercase">Observações</span>
                  <p className="text-slate-700 italic mt-0.5">{selectedItem.notes}</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              {selectedItem.productUrl ? (
                <a
                  href={selectedItem.productUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all"
                >
                  <span>Abrir Link da Loja</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              ) : <div />}

              <button
                type="button"
                onClick={() => setSelectedItem(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors"
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistoryArchiveModal;
